import React, { useState, useEffect, useRef } from "react";
import axios from "../../api/axiosInstance";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { GiLotus } from "react-icons/gi";
import { FaShieldAlt } from "react-icons/fa";

const OTP_LENGTH = 6;

const VerifyOTP = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [otpDigits, setOtpDigits] = useState(Array(OTP_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const inputRefs = useRef([]);

  const otp = otpDigits.join("");

  useEffect(() => {
    const savedEmail = localStorage.getItem("pendingUserEmail");
    if (savedEmail) {
      setEmail(savedEmail);
    } else {
      toast.error("No registration found, please register first.");
      navigate("/register");
    }
  }, [navigate]);

  const handleDigitChange = (index, value) => {
    // Only allow a single digit per box
    const digit = value.replace(/[^0-9]/g, "").slice(-1);
    const next = [...otpDigits];
    next[index] = digit;
    setOtpDigits(next);

    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/[^0-9]/g, "");
    if (!pasted) return;
    e.preventDefault();
    const next = Array(OTP_LENGTH).fill("");
    pasted
      .slice(0, OTP_LENGTH)
      .split("")
      .forEach((char, i) => (next[i] = char));
    setOtpDigits(next);
    const lastIndex = Math.min(pasted.length, OTP_LENGTH) - 1;
    if (lastIndex >= 0) inputRefs.current[lastIndex]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!otp.trim() || otp.length < OTP_LENGTH) {
      toast.error("Please enter the OTP.");
      return;
    }

    setLoading(true);
    try {
      await axios.post("/api/accounts/verify-otp/", { email, otp });
      toast.success("Account verified successfully! You can now login.");
      localStorage.removeItem("pendingUserEmail");
      navigate("/login");
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.error ||
          "OTP verification failed. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setResendLoading(true);
    try {
      await axios.post("/api/accounts/resend-otp/", { email });
      toast.success("OTP resent successfully. Check your email.");
      setOtpDigits(Array(OTP_LENGTH).fill(""));
      inputRefs.current[0]?.focus();
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.error ||
          "Failed to resend OTP. Please try again.",
      );
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center items-center bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 px-4 py-8 sm:py-10 relative overflow-hidden">
      <Toaster position="top-center" />

      {/* Soft decorative lotus watermarks, hidden on very small screens */}
      <GiLotus className="hidden sm:block absolute -top-8 -left-8 text-amber-200/40 text-[140px] sm:text-[180px] rotate-12 pointer-events-none" />
      <GiLotus className="hidden sm:block absolute -bottom-10 -right-6 text-orange-200/40 text-[140px] sm:text-[180px] -rotate-12 pointer-events-none" />

      <div className="relative z-10 bg-white/95 backdrop-blur-sm shadow-xl ring-1 ring-amber-100 rounded-2xl p-6 sm:p-8 w-full max-w-sm sm:max-w-md">
        {/* Brand mark */}
        <div className="flex flex-col items-center mb-4">
          <div className="bg-gradient-to-br from-amber-400 to-orange-600 text-white rounded-full p-3 mb-3 shadow-md">
            <FaShieldAlt className="text-xl sm:text-2xl" />
          </div>
          <span className="uppercase tracking-wide text-xs font-medium text-amber-700">
            Jain Spiritual Matrimony
          </span>
        </div>

        <h2 className="text-xl sm:text-2xl font-bold text-center text-orange-700 mb-2">
          Verify Your Account
        </h2>
        <p className="text-center text-gray-500 text-sm sm:text-base mb-6 px-2">
          Enter the OTP sent to{" "}
          <span className="font-medium text-gray-700 break-all">{email}</span>
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          {/* OTP digit boxes */}
          <div
            className="flex justify-center gap-1.5 sm:gap-2.5"
            onPaste={handlePaste}
          >
            {otpDigits.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleDigitChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-9 h-11 sm:w-12 sm:h-14 text-center text-lg sm:text-xl font-semibold border-2 border-amber-200 rounded-lg focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors"
                required
              />
            ))}
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold py-2.5 sm:py-3 rounded-full w-full text-sm sm:text-base transition-colors ${
              loading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {loading ? "Verifying..." : "Verify OTP"}
          </button>
        </form>

        <button
          onClick={handleResendOTP}
          disabled={resendLoading}
          className={`mt-4 w-full text-center text-sm text-orange-700 underline hover:text-orange-800 transition-colors ${
            resendLoading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {resendLoading ? "Resending OTP..." : "Resend OTP"}
        </button>
      </div>
    </div>
  );
};

export default VerifyOTP;
